import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import type { EditableForm, QuestionEntry, AnswerKind } from './CreateForm';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RawStructure {
  name: string;
  questions: number[];
  answerTypes: number[];
}

interface ModifyFormProps {
  onClose: () => void;
  onEdit: (form: EditableForm) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function optionsToKind(opts: string[]): { kind: AnswerKind; choices: string } {
  if (opts.length === 0) return { kind: 'header', choices: '' };
  if (opts.length === 1 && opts[0] === '') return { kind: 'text', choices: '' };
  return { kind: 'multichoice', choices: opts.join(', ') };
}

// ─── Component ────────────────────────────────────────────────────────────────

const ModifyForm: React.FC<ModifyFormProps> = ({ onClose, onEdit }) => {
  const [structures, setStructures] = useState<Record<string, RawStructure>>({});
  const [questions, setQuestions]   = useState<Record<string, string>>({});
  const [answerTypes, setAnswerTypes] = useState<Record<string, string[]>>({});
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const [qSnap, aSnap, sSnap] = await Promise.all([
        getDoc(doc(db, 'forms', 'questions')),
        getDoc(doc(db, 'forms', 'answer_types')),
        getDoc(doc(db, 'forms', 'structures')),
      ]);
      setQuestions(   (qSnap.data() as Record<string, string>)   || {});
      setAnswerTypes( (aSnap.data() as Record<string, string[]>) || {});
      setStructures(  (sSnap.data() as Record<string, RawStructure>) || {});
      setLoading(false);
    };
    fetch();
  }, []);

  const handleDelete = async (formId: string) => {
    if (!window.confirm('Delete this form?')) return;
    const next = { ...structures };
    delete next[formId];
    await setDoc(doc(db, 'forms', 'structures'), next);
    setStructures(next);
  };

  const handleEdit = (formId: string) => {
    const s = structures[formId];
    if (!s) return;
    const entries: QuestionEntry[] = s.questions.map((qId, i) => {
      const aId = s.answerTypes[i];
      const opts: string[] = answerTypes[String(aId)] ?? [];
      const { kind, choices } = optionsToKind(opts);
      return { localId: crypto.randomUUID(), text: questions[String(qId)] ?? '', kind, choices };
    });
    onEdit({ form_id: Number(formId), name: s.name, entries });
  };

  const sortedIds = Object.keys(structures).sort((a, b) => Number(a) - Number(b));

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white h-5/6 w-11/12 lg:w-3/4 xl:w-1/2 p-8 rounded-3xl shadow-2xl relative overflow-auto">
        <button className="btn btn-sm btn-circle absolute right-2 top-2" onClick={onClose}>✕</button>
        <h2 className="text-3xl font-semibold text-gray-800 mb-6">Manage Forms</h2>

        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : sortedIds.length === 0 ? (
          <p className="text-gray-500">No forms yet.</p>
        ) : (
          <div className="space-y-4">
            {sortedIds.map(id => {
              const s = structures[id];
              return (
                <div key={id} className="bg-gray-50 p-4 rounded-xl flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800">
                      <span className="text-gray-400 text-sm mr-2">#{id}</span>
                      {s.name}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {s.questions?.length ?? 0} question{s.questions?.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button className="btn btn-sm btn-outline" onClick={() => handleEdit(id)}>Edit</button>
                    <button className="btn btn-sm btn-error btn-outline" onClick={() => handleDelete(id)}>Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModifyForm;
